// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { AddCommunicationDialogComponent } from 'src/app/shared/components/dialogs/add-communication-dialog/add-communication-dialog.component';
import { PhonebookContact } from 'src/app/shared/models/phonebook/phonebook.model';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Component({
  selector: 'app-edit-phonebook-dialog',
  templateUrl: './edit-phonebook-dialog.component.html',
  styleUrls: ['./edit-phonebook-dialog.component.scss']
})
export class EditPhonebookContactDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  phonebook: PhonebookContact[] = new PhonebookContact()[''];
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  loggedUserRoleType: string;

  // BEGIN - Edit phonebook contact form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.save'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-save',
      inline: true,
    },
  };
  // END - Edit phonebook contact form

  createdAt = "";
  dialogIsLoading = true;

  propertiesMaster: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<EditPhonebookContactDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public newMessagedialogRef: MatDialogRef<AddCommunicationDialogComponent>,
    public newMessageDialog: MatDialog,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private helperService: HelperService,
    private propertiesService: PropertiesService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    this.authService.loggedUserRequestedRoleType$.subscribe((res) => {
      if (res) {
        this.loggedUserRoleType = res;
        if (
          this.loggedUserRoleType === "master" ||
          this.loggedUserRoleType === "collaborator"
        ) {
          this.subscriptions.push(
            this.propertiesService.getPropertiesMaster().subscribe(
              (res) => {
                if (res.status) {
                  this.propertiesMaster = res.data.items;
                  // Init add form
                  this.initMasterForm();
                }
                this.dialogIsLoading = false;
              },
              (err) => {
                this.dialogIsLoading = false;
              }
            )
          );
        } else if (
          this.loggedUserRoleType === "admin" ||
          this.loggedUserRoleType === "operator"
        ) {
          this.initForm();
          this.dialogIsLoading = false;
        }
      }
    });
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initMasterForm(): void {
    // Set model (shallow clone to avoid mutating the table row element passed by reference)
    this.model = Object.assign({}, this.data.phonebookContact);
    this.createdAt = this.data.phonebookContact.createdAt;
    if (
      this.data.phonebookContact.propertyId && 
      this.data.phonebookContact.propertyId._id
    ) {
      this.model.propertyId = this.data.phonebookContact.propertyId._id;
    }

    const propertiesOption = [];
    propertiesOption.push(
      {
        label: '',
        value: '',
      }
    )
    if (this.propertiesMaster && Array.isArray(this.propertiesMaster)) {
      this.propertiesMaster.forEach((item: any) =>
        propertiesOption.push(
          {
            label: item.name,
            value: item._id,
          }
        )
      );
    }

    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12 col-md-3',
            type: 'input',
            key: 'firstName',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.firstName'),
              placeholder: this.translateService.instant('companies.fields.phonebook.firstName'),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-md-3',
            type: 'input',
            key: 'lastName',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.lastName'),
              placeholder: this.translateService.instant('companies.fields.phonebook.lastName'),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'select',
            key: 'propertyId',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.property'),
              placeholder: this.translateService.instant('companies.fields.phonebook.property'),
              required: false,
              options: propertiesOption,
            },
          },
          {
            className: 'col-12 col-md-3',
            type: 'input',
            key: 'subui',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.subUI'),
              placeholder: this.translateService.instant('companies.fields.phonebook.subUI'),
              required: false,
              minLength: 0,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-3',
            type: 'select',
            key: 'type',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.type'),
              placeholder: this.translateService.instant('companies.fields.phonebook.type'),
              required: false,
              options: [
                {
                  label: this.translateService.instant('properties.fields.phonebook.councilor'),
                  value: 'councilor',
                },
                {
                  label: this.translateService.instant('properties.fields.phonebook.occupant'),
                  value: 'occupant',
                },
                {
                  label: this.translateService.instant('properties.fields.phonebook.provider'),
                  value: 'provider',
                },
              ]
            },
          },
          {
            className: 'col-12',
            type: 'input',
            key: 'email',
            templateOptions: {
              type: 'email',
              label: this.translateService.instant('companies.fields.phonebook.email'),
              placeholder: this.translateService.instant('companies.fields.phonebook.email'),
              required: false,
              minLength: 6,
              maxLength: 320,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'phone',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.phone'),
              placeholder: this.translateService.instant('companies.fields.phonebook.phone'),
              required: false,
              minLength: 0,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'mobile',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.mobile'),
              placeholder: this.translateService.instant('companies.fields.phonebook.mobile'),
              required: false,
              minLength: 0,
              maxLength: 50,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'notes',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.notes'),
              placeholder: this.translateService.instant('companies.fields.phonebook.notes'),
              required: false,
              maxLength: 1000,
              rows: 2,
            },
            expressionProperties: {
              'templateOptions.description': '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            }
          },
        ],
      },
    ];
  }
  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model (shallow clone to avoid mutating the table row element passed by reference)
    this.model = Object.assign({}, this.data.phonebookContact);
    this.createdAt = this.data.phonebookContact.createdAt;

    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12 col-md-3',
            type: 'input',
            key: 'firstName',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.firstName'),
              placeholder: this.translateService.instant('companies.fields.phonebook.firstName'),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-md-3',
            type: 'input',
            key: 'lastName',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.lastName'),
              placeholder: this.translateService.instant('companies.fields.phonebook.lastName'),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-md-3',
            type: 'input',
            key: 'subui',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.subUI'),
              placeholder: this.translateService.instant('companies.fields.phonebook.subUI'),
              required: false,
              minLength: 0,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-3',
            type: 'select',
            key: 'type',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.type'),
              placeholder: this.translateService.instant('companies.fields.phonebook.type'),
              required: false,
              options: [
                {
                  label: this.translateService.instant('properties.fields.phonebook.councilor'),
                  value: 'councilor',
                },
                {
                  label: this.translateService.instant('properties.fields.phonebook.occupant'),
                  value: 'occupant',
                },
                {
                  label: this.translateService.instant('properties.fields.phonebook.provider'),
                  value: 'provider',
                },
              ]
            },
          },
          {
            className: 'col-12',
            type: 'input',
            key: 'email',
            templateOptions: {
              type: 'email',
              label: this.translateService.instant('companies.fields.phonebook.email'),
              placeholder: this.translateService.instant('companies.fields.phonebook.email'),
              required: false,
              minLength: 6,
              maxLength: 320,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'phone',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.phone'),
              placeholder: this.translateService.instant('companies.fields.phonebook.phone'),
              required: false,
              minLength: 0,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'mobile',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.mobile'),
              placeholder: this.translateService.instant('companies.fields.phonebook.mobile'),
              required: false,
              minLength: 0,
              maxLength: 50,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'notes',
            templateOptions: {
              label: this.translateService.instant('companies.fields.phonebook.notes'),
              placeholder: this.translateService.instant('companies.fields.phonebook.notes'),
              required: false,
              maxLength: 1000,
              rows: 2,
            },
            expressionProperties: {
              'templateOptions.description': '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            }
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   * @param [data] optional data passed to the caller component
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    this.subscriptions.push(
      this.companiesService.updatePhonebookContact(this.data.companyId, this.data.phonebookContact._id, model)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.onClose(true);
              return;
            }
            this.toastrService.error(this.translateService.instant('common.errors.unknown_server_error'));
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Handle new message dialog open
   *
   * @since 1.0.0
   */
  onAddCommunication(): void {
    if (!this.model) {
      return;
    }

    // First get phonebook list from company
    this.subscriptions.push(
      this.companiesService.getPhonebookList(this.data.companyId).subscribe(
        res => {
          if (res.status) {
            this.phonebook = res.data.items;
            // Open dialog
            const newMessageDialogRef = this.newMessageDialog.open(AddCommunicationDialogComponent, {
              minWidth: '60%',
              maxHeight: '98vh',
              data: {
                recipients: [{
                  email: this.model.email,
                  name: `${this.model.firstName} ${this.model.lastName}`,
                }],
                phonebook: this.phonebook,
              },
              disableClose: true,
            });

            // On dialog closing, check if something is changed
            // If so, let's reload table data
            this.subscriptions.push(newMessageDialogRef.afterClosed()
              .subscribe(
                res2 => {
                  if (typeof res2 !== 'undefined' && res2.sendedMessage) {
                    this.toastrService.success(this.translateService.instant('communications.add.sendedSuccessfully'))
                    return;
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              ));
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }
}
